import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, Calendar, CreditCard, Trophy, ClipboardList, 
  MessageSquare, Layout, Shield, Smartphone, BarChart3,
  CheckCircle, ArrowRight, Zap, Star, ChevronRight,
  UserCheck, Bell, FileText, Settings, Target
} from 'lucide-react';
import { Button, CardCorner, AnimatedBorder } from '../ui';

export function Academies() {
  const { t } = useTranslation();
  const [selectedFeature, setSelectedFeature] = useState(0);
  const [activeTab, setActiveTab] = useState<'features' | 'roles'>('features');

  const FEATURES = useMemo(() => [
    {
      id: 'players',
      icon: Users,
      title: t('academies.playersDetailTitle'),
      description: t('academies.playersDetailDesc'),
      details: [
        t('academies.playersDetail1'),
        t('academies.playersDetail2'),
        t('academies.playersDetail3'),
        t('academies.playersDetail4'),
        t('academies.playersDetail5'),
        t('academies.playersDetail6'),
      ],
      color: 'pitch',
    },
    {
      id: 'attendance',
      icon: ClipboardList,
      title: t('academies.attendanceDetailTitle'),
      description: t('academies.attendanceDetailDesc'),
      details: [
        t('academies.attendanceDetail1'),
        t('academies.attendanceDetail2'),
        t('academies.attendanceDetail3'),
        t('academies.attendanceDetail4'),
        t('academies.attendanceDetail5'),
        t('academies.attendanceDetail6'),
      ],
      color: 'cyan',
    },
    {
      id: 'payments',
      icon: CreditCard,
      title: t('academies.paymentsDetailTitle'),
      description: t('academies.paymentsDetailDesc'),
      details: [
        t('academies.paymentsDetail1'),
        t('academies.paymentsDetail2'),
        t('academies.paymentsDetail3'),
        t('academies.paymentsDetail4'),
        t('academies.paymentsDetail5'),
        t('academies.paymentsDetail6'),
      ],
      color: 'gold',
    },
    {
      id: 'calendar',
      icon: Calendar,
      title: t('academies.calendarDetailTitle'),
      description: t('academies.calendarDetailDesc'),
      details: [
        t('academies.calendarDetail1'),
        t('academies.calendarDetail2'),
        t('academies.calendarDetail3'),
        t('academies.calendarDetail4'),
        t('academies.calendarDetail5'),
        t('academies.calendarDetail6'),
      ],
      color: 'purple',
    },
    {
      id: 'matches',
      icon: Trophy,
      title: t('academies.matchesDetailTitle'),
      description: t('academies.matchesDetailDesc'),
      details: [
        t('academies.matchesDetail1'),
        t('academies.matchesDetail2'),
        t('academies.matchesDetail3'),
        t('academies.matchesDetail4'),
        t('academies.matchesDetail5'),
        t('academies.matchesDetail6'),
      ],
      color: 'pitch',
    },
    {
      id: 'tactical',
      icon: Layout,
      title: t('academies.tacticalDetailTitle'),
      description: t('academies.tacticalDetailDesc'),
      details: [
        t('academies.tacticalDetail1'),
        t('academies.tacticalDetail2'),
        t('academies.tacticalDetail3'),
        t('academies.tacticalDetail4'),
        t('academies.tacticalDetail5'),
        t('academies.tacticalDetail6'),
      ],
      color: 'cyan',
    },
    {
      id: 'messages',
      icon: MessageSquare,
      title: t('academies.commDetailTitle'),
      description: t('academies.commDetailDesc'),
      details: [
        t('academies.commDetail1'),
        t('academies.commDetail2'),
        t('academies.commDetail3'),
        t('academies.commDetail4'),
        t('academies.commDetail5'),
        t('academies.commDetail6'),
      ],
      color: 'gold',
    },
    {
      id: 'categories',
      icon: Target,
      title: t('academies.catDetailTitle'),
      description: t('academies.catDetailDesc'),
      details: [
        t('academies.catDetail1'),
        t('academies.catDetail2'),
        t('academies.catDetail3'),
        t('academies.catDetail4'),
        t('academies.catDetail5'),
        t('academies.catDetail6'),
      ],
      color: 'purple',
    },
  ], [t]);

  const ROLES = useMemo(() => [
    {
      role: t('academies.directorRole'),
      description: t('academies.directorRoleDesc'),
      features: [
        t('academies.directorFeature1'),
        t('academies.directorFeature2'),
        t('academies.directorFeature3'),
        t('academies.directorFeature4'),
      ],
      icon: Shield,
    },
    {
      role: t('academies.coachRole'),
      description: t('academies.coachRoleDesc'),
      features: [
        t('academies.coachFeature1'),
        t('academies.coachFeature2'),
        t('academies.coachFeature3'),
        t('academies.coachFeature4'),
      ],
      icon: UserCheck,
    },
    {
      role: t('academies.guardianRole'),
      description: t('academies.guardianRoleDesc'),
      features: [
        t('academies.guardianFeature1'),
        t('academies.guardianFeature2'),
        t('academies.guardianFeature3'),
        t('academies.guardianFeature4'),
      ],
      icon: Users,
    },
    {
      role: t('academies.playerRole'),
      description: t('academies.playerRoleDesc'),
      features: [
        t('academies.playerFeature1'),
        t('academies.playerFeature2'),
        t('academies.playerFeature3'),
        t('academies.playerFeature4'),
      ],
      icon: Star,
    },
  ], [t]);

  const BENEFITS = useMemo(() => [
    t('academies.benefit1'),
    t('academies.benefit2'),
    t('academies.benefit3'),
  ], [t]);

  return (
    <section id="academies" className="relative py-24 md:py-32 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-brand-darkNavy via-brand-navy to-brand-darkNavy" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,rgba(0,255,170,0.08)_0%,transparent_60%)]" />
      
      {/* Animated grid */}
      <div 
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `linear-gradient(rgba(0,255,170,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(0,255,170,0.5) 1px, transparent 1px)`,
          backgroundSize: '40px 40px',
        }}
      />

      <div className="relative container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <motion.div 
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-fifa-teal/10 border border-fifa-teal/30 mb-6"
            initial={{ scale: 0.9, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true }}
          >
            <Settings className="w-4 h-4 text-fifa-teal" />
            <span className="text-sm font-bold text-fifa-teal tracking-wider">{t('academies.badge')}</span>
          </motion.div>
          
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6">
            {t('academies.sectionTitle1')}
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-fifa-teal via-cyan-400 to-fifa-teal">
              {t('academies.sectionTitle2')}
            </span>
          </h2>
          
          <p className="text-lg md:text-xl text-dark-300 max-w-3xl mx-auto">
            {t('academies.sectionDesc')}
          </p>
        </motion.div>

        {/* Benefits Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="mb-16"
        >
          <div className="flex flex-wrap justify-center gap-4">
            {BENEFITS.map((benefit, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 * i }}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-brand-navy/60 border border-brand-lightBlue/20"
              >
                <CheckCircle className="w-4 h-4 text-fifa-teal flex-shrink-0" />
                <span className="text-sm text-dark-300">{benefit}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Tabs */}
        <div className="flex justify-center gap-4 mb-12">
          <button
            onClick={() => setActiveTab('features')}
            className={`px-6 py-3 rounded-xl font-bold transition-all duration-300 ${
              activeTab === 'features'
                ? 'bg-fifa-teal text-brand-darkNavy'
                : 'bg-brand-navy/60 text-dark-300 hover:text-white border border-brand-lightBlue/20'
            }`}
          >
            {t('academies.featuresTab')}
          </button>
          <button
            onClick={() => setActiveTab('roles')}
            className={`px-6 py-3 rounded-xl font-bold transition-all duration-300 ${
              activeTab === 'roles'
                ? 'bg-fifa-teal text-brand-darkNavy'
                : 'bg-brand-navy/60 text-dark-300 hover:text-white border border-brand-lightBlue/20'
            }`}
          >
            {t('academies.rolesTab')}
          </button>
        </div>

        <AnimatePresence mode="wait">
          {activeTab === 'features' && (
            <motion.div
              key="features"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              {/* Features Grid */}
              <div className="grid lg:grid-cols-3 gap-8 mb-16">
                {/* Feature List */}
                <div className="lg:col-span-1 space-y-2">
                  {FEATURES.map((feature, i) => {
                    const Icon = feature.icon;
                    const isSelected = selectedFeature === i;
                    
                    return (
                      <motion.button
                        key={feature.id}
                        onClick={() => setSelectedFeature(i)}
                        whileHover={{ x: 5 }}
                        className={`
                          w-full p-4 rounded-xl text-left transition-all duration-300 flex items-center gap-4
                          ${isSelected 
                            ? 'bg-gradient-to-r from-fifa-teal/20 to-transparent border-l-4 border-fifa-teal' 
                            : 'bg-brand-navy/40 hover:bg-brand-navy/60 border-l-4 border-transparent'
                          }
                        `}
                      >
                        <div className={`
                          w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0
                          ${isSelected ? 'bg-fifa-teal/20' : 'bg-brand-lightBlue/10'}
                        `}>
                          <Icon className={`w-5 h-5 ${isSelected ? 'text-fifa-teal' : 'text-dark-400'}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`font-bold ${isSelected ? 'text-white' : 'text-dark-300'}`}>
                            {feature.title}
                          </p>
                        </div>
                        {isSelected && (
                          <ChevronRight className="w-5 h-5 text-fifa-teal flex-shrink-0" />
                        )}
                      </motion.button>
                    );
                  })}
                </div>

                {/* Feature Detail */}
                <div className="lg:col-span-2">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={selectedFeature}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.3 }}
                      className="relative bg-gradient-to-br from-brand-navy/80 to-brand-darkNavy/80 backdrop-blur-xl rounded-2xl border border-brand-lightBlue/20 p-8 h-full"
                    >
                      <CardCorner position="top-left" />
                      <CardCorner position="bottom-right" />
                      
                      {/* Glow effect */}
                      <div className="absolute -top-20 -right-20 w-40 h-40 bg-fifa-teal/10 rounded-full blur-3xl" />
                      
                      <div className="relative">
                        <div className="flex items-center gap-4 mb-6">
                          {(() => {
                            const Icon = FEATURES[selectedFeature].icon;
                            return (
                              <div className="w-14 h-14 rounded-xl bg-fifa-teal/20 flex items-center justify-center">
                                <Icon className="w-7 h-7 text-fifa-teal" />
                              </div>
                            );
                          })()}
                          <div>
                            <h3 className="text-2xl font-black text-white">
                              {FEATURES[selectedFeature].title}
                            </h3>
                            <p className="text-dark-400">
                              {FEATURES[selectedFeature].description}
                            </p>
                          </div>
                        </div>

                        <div className="grid sm:grid-cols-2 gap-3">
                          {FEATURES[selectedFeature].details.map((detail, i) => (
                            <motion.div
                              key={i}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: i * 0.05 }}
                              className="flex items-center gap-3 p-3 rounded-lg bg-brand-darkNavy/50 border border-brand-lightBlue/10"
                            >
                              <CheckCircle className="w-5 h-5 text-fifa-teal flex-shrink-0" />
                              <span className="text-dark-300">{detail}</span>
                            </motion.div>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  </AnimatePresence>
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === 'roles' && (
            <motion.div
              key="roles"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              {/* Roles Grid */}
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
                {ROLES.map((role, i) => {
                  const Icon = role.icon;
                  return (
                    <motion.div
                      key={role.role}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.1 }}
                      whileHover={{ y: -5, scale: 1.02 }}
                      className="relative bg-brand-navy/60 backdrop-blur-xl rounded-2xl border border-brand-lightBlue/20 p-6 overflow-hidden group"
                    >
                      <CardCorner position="top-left" />
                      <CardCorner position="bottom-right" />
                      
                      {/* Glow on hover */}
                      <div className="absolute inset-0 bg-fifa-teal/5 opacity-0 group-hover:opacity-100 transition-opacity" />
                      
                      <div className="relative">
                        <div className="w-12 h-12 rounded-xl bg-fifa-teal/20 flex items-center justify-center mb-4 group-hover:bg-fifa-teal/30 transition-colors">
                          <Icon className="w-6 h-6 text-fifa-teal" />
                        </div>
                        
                        <h4 className="text-xl font-bold text-white mb-2">{role.role}</h4>
                        <p className="text-dark-400 text-sm mb-4">{role.description}</p>
                        
                        <div className="space-y-2">
                          {role.features.map((feature, j) => (
                            <div key={j} className="flex items-center gap-2">
                              <Zap className="w-3 h-3 text-fifa-teal" />
                              <span className="text-sm text-dark-300">{feature}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* App Preview / Mockup */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="mb-16"
        >
          <div className="relative bg-gradient-to-br from-brand-navy/90 to-brand-darkNavy/90 backdrop-blur-xl rounded-3xl border border-fifa-teal/20 p-8 md:p-12 overflow-hidden">
            {/* Decorative elements */}
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-fifa-teal to-transparent" />
            <div className="absolute -top-40 -right-40 w-80 h-80 bg-fifa-teal/10 rounded-full blur-3xl" />
            
            <div className="relative grid md:grid-cols-2 gap-8 items-center">
              {/* Left - Content */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Smartphone className="w-5 h-5 text-fifa-teal" />
                  <span className="text-fifa-teal font-bold">{t('academies.availableWebMobile')}</span>
                </div>
                
                <h3 className="text-3xl md:text-4xl font-black text-white mb-4">
                  {t('academies.manageAnywhere')}
                </h3>
                
                <p className="text-dark-300 mb-6">
                  {t('academies.manageAnywhereDesc')}
                </p>

                <div className="grid grid-cols-2 gap-4 mb-8">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-fifa-teal/20 flex items-center justify-center">
                      <Bell className="w-5 h-5 text-fifa-teal" />
                    </div>
                    <span className="text-dark-300 text-sm">{t('academies.pushNotifications')}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-fifa-teal/20 flex items-center justify-center">
                      <BarChart3 className="w-5 h-5 text-fifa-teal" />
                    </div>
                    <span className="text-dark-300 text-sm">{t('academies.autoReports')}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-fifa-teal/20 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-fifa-teal" />
                    </div>
                    <span className="text-dark-300 text-sm">{t('academies.digitalIds')}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-fifa-teal/20 flex items-center justify-center">
                      <Shield className="w-5 h-5 text-fifa-teal" />
                    </div>
                    <span className="text-dark-300 text-sm">{t('academies.secureData')}</span>
                  </div>
                </div>

                <a 
                  href="mailto:contact@btalentstar.com?subject=Consulta%20Software%20Academias"
                  className="inline-block"
                >
                  <Button
                    variant="primary"
                    size="lg"
                    icon={<ArrowRight className="w-5 h-5" />}
                    iconPosition="right"
                  >
                    {t('academies.requestDemoBtn')}
                  </Button>
                </a>
              </div>

              {/* Right - Stats */}
              <div className="grid grid-cols-2 gap-4">
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  className="bg-brand-darkNavy/60 rounded-2xl p-6 border border-brand-lightBlue/20 text-center"
                >
                  <p className="text-4xl font-black text-fifa-teal">500+</p>
                  <p className="text-dark-400 text-sm mt-1">{t('academies.statPlayersManaged')}</p>
                </motion.div>
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  className="bg-brand-darkNavy/60 rounded-2xl p-6 border border-brand-lightBlue/20 text-center"
                >
                  <p className="text-4xl font-black text-brand-yellow">15+</p>
                  <p className="text-dark-400 text-sm mt-1">{t('academies.statActiveAcademies')}</p>
                </motion.div>
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  className="bg-brand-darkNavy/60 rounded-2xl p-6 border border-brand-lightBlue/20 text-center"
                >
                  <p className="text-4xl font-black text-white">10K+</p>
                  <p className="text-dark-400 text-sm mt-1">{t('academies.statAttendanceRecords')}</p>
                </motion.div>
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  className="bg-brand-darkNavy/60 rounded-2xl p-6 border border-brand-lightBlue/20 text-center"
                >
                  <p className="text-4xl font-black text-cyan-400">99%</p>
                  <p className="text-dark-400 text-sm mt-1">{t('academies.statUptime')}</p>
                </motion.div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="text-center"
        >
          <AnimatedBorder className="inline-block">
            <div className="px-8 py-6 bg-brand-navy/80">
              <p className="text-white font-bold text-lg mb-2">
                {t('academies.ctaQuestion')}
              </p>
              <p className="text-dark-400 mb-4">
                {t('academies.ctaJoin')}
              </p>
              <div className="flex flex-wrap justify-center gap-3">
                <a href="mailto:contact@btalentstar.com?subject=Demo%20Software%20Academias">
                  <Button variant="primary" icon={<ArrowRight className="w-4 h-4" />} iconPosition="right">
                    {t('academies.scheduleFreeDemo')}
                  </Button>
                </a>
                <a href="mailto:contact@btalentstar.com?subject=Demo%20Software%20Academias" target="_blank" rel="noopener noreferrer">
                  <Button variant="outline">
                    {t('academies.talkWhatsApp')}
                  </Button>
                </a>
              </div>
            </div>
          </AnimatedBorder>
        </motion.div>
      </div>
    </section>
  );
}
